import { useState, useEffect, useRef } from "react";

// HELPERS
import api from "@helpers/api";
import { logUserAction } from "@helpers/utils";


// HOOKS
import useResponsive from "@hooks/useResponsive";

const useCustomersTable = props => {
  const isMobileOrTablet = useResponsive("down", "md");
  const {
    inline,
    showHeader,
    width,
    partner,
    user,
    advisor,
    customers,
    setCustomerState,
    caseFilterContext,
    nextStepOptions,
    onWorkflowSwitched
  } = props;

  // Get unique tags from all customers and order them alphabetically
  const allClientTags = [];
  customers?.forEach(customer => {
    if (customer.tags && customer.tags.length > 0) {
      customer.tags.forEach(tag => {
        if (!allClientTags.includes(tag)) {
          allClientTags.push(tag);
        }
      });
    }
  });
  allClientTags.sort();

  //   // Get unique tags from all customers and sort them by occurrence
  // const allCustomerTags = [];
  // const tagCounts = {};
  
  // customers?.forEach(customer => {
  //   if (customer.tags && customer.tags.length > 0) {
  //     customer.tags.forEach(tag => {
  //       tagCounts[tag] = (tagCounts[tag] || 0) + 1;
  //       if (!allCustomerTags.includes(tag)) {
  //         allCustomerTags.push(tag);
  //       }
  //     });
  //   }
  // });  
  // allCustomerTags.sort((a, b) => tagCounts[b] - tagCounts[a]);

  const [loaded, setLoaded] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [tagFilter, setTagFilter] = useState("all");
  const [copyList, setCopyList] = useState([]);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerModalView, setCustomerModalView] = useState("overview");
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [newCustomerAdded, setNewCustomerAdded] = useState(null);
  const [allPartnerTags, setAllPartnerTags] = useState([]);
  const searchInputRef = useRef(null);

  let columns = [];
  columns.push(
    {
      header: "",
      id: "menuButton",
      accessor: "menuButton",
      width: "2.5%",
      disablePadding: true,
      disableSort: true
    },
    ...(!isMobileOrTablet
      ? [
          {
            header: "",
            id: "infoIcon",
            accessor: "infoIcon",
            width: "2.5%",
            disablePadding: true,
            disableSort: true
          }
        ]
      : []),
    {
      header: "Namn",
      accessor: "name",
      width: "40.5%"
    },
    {
      header: `E-postadress`,
      accessor: "email",
      width: "25%"
    },
    {
      header: `Senaste rådgivning`,
      accessor: "last_signed_document",
      width: "32%"
    }
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setPage(0);
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const updateCustomers = (customer, notesCount = false) => {
    // find existing index
    let index = customers.findIndex(
      c =>
        (c.client_id &&
          customer.clientId &&
          c.client_id === customer.clientId) ||
        (c.clientId && customer.clientId && c.clientId === customer.clientId) ||
        (c.client_id &&
          customer.client_id &&
          c.client_id === customer.client_id)
    );

    if (notesCount !== false) {
      customers[index].notesCount = notesCount;
      return setCopyList(customers);
    }

    const isNewCustomer = index === -1;
    // if not found, add to end (new customer)
    if (isNewCustomer) {
      index = customers.length;
      setNewCustomerAdded(customer);
    }
    // update customers with new customer record
    customers[index] = customer;
    // sort alphabetically
    customers.sort((a, b) => {
      const aName = a.name === null ? "" : a.name;
      const bName = b.name === null ? "" : b.name;
      return aName.localeCompare(bName, "sv", { sensitivity: "base" });
    });

    // if a customer is selected, update it (edit from customer modal)
    if (selectedCustomer) setSelectedCustomer(customer);

    return setCopyList(customers);
  };

  const requestSearch = searched => {
    if (customers) {
      if (page > 0) setPage(0);
      const searchString = searched.toLowerCase();
      setCopyList(
        customers.filter(
          customer =>
            ((customer.first_name + " " + customer.last_name)
              .toLowerCase()
              .includes(searchString) ||
              customer.id?.toLowerCase().includes(searchString) ||
              customer.name?.toLowerCase().includes(searchString)) &&
            (tagFilter === "all" ||
              !tagFilter ||
              (customer.tags && customer.tags.includes(tagFilter)))
        )
      );
    }
  };

  const handleTagFilterChange = tag => {
    setTagFilter(tag);
    // Clear search input
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    if (page > 0) setPage(0);

    if (tag === "" || tag === "all") {
      // Clear filter
      setCopyList(customers);
    } else {
      // Apply filter
      setCopyList(
        customers.filter(
          customer => customer.tags && customer.tags.includes(tag)
        )
      );
    }
  };

  const toggleAddCustomerModal = action => {
    if (!customerModalOpen && (action === "add" || addCustomerModalOpen))
      setSelectedCustomer(null);

    if (addCustomerModalOpen) {
      // disable/enable background content scrolling while modal is open
      document.documentElement.style.overflow = "auto";
    } else {
      document.documentElement.style.overflow = "hidden";
    }

    setAddCustomerModalOpen(!addCustomerModalOpen);
  };

  const handleClientClick = (client, isNotesClick) => async e => {
    e.preventDefault();
    setCustomerState(
      {
        isCompany: false,
        ssnOrg: client.id ? client.id.replace("-", "") : "",
        isNewCase: null,
        standaloneApplicationsChecked: [],
        needsNewDocumentation: false,
        kycCaseId: null,
        kycInfoLoading: false,
        overrideNewDocumentation: false,
        casesLoading: false,
        cases: null,
        selectedCaseId: null,
        selectedCaseIndex: -1,
        customerModalIsOpen: true
      },
      () => {
        setNewCustomerAdded(null);
        setCustomerModalOpen(true);
        if (isNotesClick) setCustomerModalView("notes");
        setSelectedCustomer(client);
        document.documentElement.style.overflow = "hidden";
      }
    );
  };

  const handleClientModalClose = () => {
    setCustomerState(
      {
        isCompany: false,
        ssnOrg: "",
        customerModalIsOpen: false
      },
      () => {
        setCustomerModalView("overview");
        setCustomerModalOpen(false);
        setSelectedCustomer(null);
      }
    );
  };

  useEffect(() => {
    // Fetch all partner tags
    const fetchAllPartnerTags = async () => {
      try {
        const partnerTags = await api("partner/tags");
        if (partnerTags) {
          setAllPartnerTags(partnerTags);
        }
      } catch (error) {
        console.error("Error fetching partner tags:", error);
      }
    };
    fetchAllPartnerTags();
  }, [partner]);

  useEffect(() => {
    // Only set copyList if customers is an array (fetching data completed)
    if (!Array.isArray(customers)) return;

    setCopyList(customers);
    logUserAction({
      action: "NAV_START_PAGE"
    });
    setLoaded(true);
  }, [customers]);

  return {
    loaded,
    inline,
    showHeader,
    width,
    isMobileOrTablet,
    partner,
    user,
    advisor,
    columns,
    copyList,
    allPartnerTags,
    allClientTags,
    tagFilter,
    page,
    setPage,
    order,
    orderBy,
    rowsPerPage,
    customerModalOpen,
    customerModalView,
    selectedCustomer,
    searchInputRef,
    setSelectedCustomer,
    setRowsPerPage,
    handleChangePage,
    handleRequestSort,
    requestSearch,
    handleTagFilterChange,
    toggleAddCustomerModal,
    handleClientClick,
    handleClientModalClose,
    updateCustomers,
    caseFilterContext,
    nextStepOptions,
    setCustomerState,
    onWorkflowSwitched,
    addCustomerModalOpen,
    newCustomerAdded
  };
};

export default useCustomersTable;
